<script>
  import { ticks, nice } from "d3-array";
  import { clamp, formatNumber, withSymbol } from "../util.js";

  import Table from "./Table.svelte";

  //props
  export let title = "";
  export let data = [];
  //`min` and `max` will clamp the bars' lengths
  export let max = Infinity;
  $: if (!max && max != 0) max = Infinity;
  export let min = 0;
  $: if (!min && min != 0) min = 0;
  export let fill = "#003366";
  export let description = "";
  export let source = "Fox news";
  export let source_url = "https://foxnews.com";
  export let brand_logo = "news";
  export let numberFormat = "";
  export let last_updated = "February 30, 3033";

  //layout specs, accessors, position calculators

  //calculate the labels values and make them "nice", based on the min and max values
  $: data_ticks = ticks(
    //...nice(min, max),
    ...nice(min, Math.min(Math.max(...data.map((d) => d.data)), max), 5),
    5
  );
  $: max_tick = Math.max(...data_ticks);
  $: min_tick = Math.min(...data_ticks);

  //accepts a data point and returns its width, as a decimal ie 0.85 (for 85% width)
  const width = (d, max, min) => {
    let numerator = clamp(d.data, max, min) - min;
    let denominator = max - min;
    return numerator / denominator;
  };

  //misc
  //used for testing and development
  /*
  const set_random_data = () => {
    let out = [];
    let num_bars = Math.random() * 20;
    for (let i = 0; i < num_bars; i++) {
      let next = {
        label: `Wizard of the Dark Arts ${i}`,
        data: Math.random() * 2000,
        fill: Math.random() > 0.45 && random_fill(),
      };
      out.push(next);
    }
    data = out;
  };
  const random_fill = (a = 1.0) => {
    let r = (0xff * Math.random()) | (0 << 4);
    let g = (0xff * Math.random()) | (0 << 2);
    let b = (0xff * Math.random()) | 0;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };
    */
</script>

<!--
<button on:click={set_random_data}>bars</button>
<button on:click={() => (fill = random_fill())}>fill</button>
<input bind:value={description} />
<input bind:value={title} />
<label>
  Max
  <input type="number" bind:value={max} />
</label>
<label>
  Min
  <input type="number" bind:value={min} />
</label>
<label>
  Number Format
  <input type="text" bind:value={numberFormat} />
</label>
<pre>
  {JSON.stringify(data_ticks, null, 2)}
</pre>
-->

{#if title || description}
  <header class="heading">
    {#if title}
      <h1 class="title">{title}</h1>
    {/if}
    {#if description}
      <p class="dek" id="description">{description}</p>
    {/if}
  </header>
{/if}
<main>
  <div id="grid_container">
    <div id="dead_space" class="left" />
    <div id="x_axis">
      {#each data_ticks as tick, i}
        <div class="label" style="width: 0px;">
          <div>
            {withSymbol(formatNumber(tick, numberFormat), numberFormat)}
          </div>
        </div>
      {/each}
    </div>
    <div id="y_axis" class="left">
      {#each data as d, i}
        <div class="label">
          {d.label}
        </div>
      {/each}
    </div>
    <div id="content_area">
      <div id="gridlines" style="pointer-events: none;">
        {#each data_ticks as tick, i}
          <div class="gridline" />
        {/each}
      </div>
      {#each data as d, i}
        <div
          class="bar"
          style="background-color: {d.fill || fill}; width: {width(
            d,
            max_tick,
            min_tick
          ) * 100}%;"
        />
      {/each}
    </div>
  </div>
  <Table {data} {numberFormat} />
</main>

<footer class="footing">
  {#if source || last_updated}
    <p>
      {#if source}
        <span
          >Source:
          {#if source_url}
            <a href={source_url} target="_top">{source}</a>.
          {:else}
            {source}
          {/if}
        </span>
      {/if}
      {#if last_updated}
        <span>
          Last Updated: {last_updated}.
        </span>
      {/if}
    </p>
  {/if}

  {#if brand_logo === "business"}
    <img
      class="logo"
      width="48px"
      height="36px"
      src="https://static.foxnews.com/static/orion/styles/img/fox-business/s/logos/fox-business-2-color.svg"
      alt="Fox Business"
    />
  {:else}
    <img
      class="logo"
      width="36px"
      height="36px"
      src="https://static.foxnews.com/static/orion/styles/img/fox-news/s/logos/fox-news-logo.svg"
      alt="Fox News"
    />
  {/if}
</footer>

<style>
  #grid_container {
    display: grid;
    grid-template-columns: min-content 1fr;
    grid-template-rows: auto 1fr;
    padding-right: 32px; /* add to 16px page padding */
    margin-bottom: 24px;
  }

  #content_area {
    border-left: 1px solid black;
    position: relative;
  }

  #x_axis .label,
  #y_axis .label {
    color: #222;
    font-weight: bold;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  #x_axis,
  #y_axis,
  #content_area {
    display: inline-flex;
  }

  #x_axis {
    justify-content: space-between;
    padding-bottom: 8px;
  }

  #x_axis .label {
    overflow: visible;
    display: flex;
    justify-content: center;
    font-size: 10px;
    line-height: 11px;
  }

  #y_axis {
    padding-right: 16px;
  }

  #y_axis .label {
    color: #000;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.25px;
    text-align: right;
  }

  #y_axis,
  #content_area {
    flex-direction: column;
    justify-content: space-around;
  }

  #gridlines {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
  }

  .gridline {
    height: 100%;
    width: 1px;
    background-color: #eaeaea;
    position: relative;
    z-index: -1;
  }

  .bar {
    background: #003366;
    height: 24px;
    margin: 4px 0;
  }

  @media (min-width: 672px) {
    #grid_container {
      padding-left: 8px; /* add to 16px page padding */
    }

    .bar {
      height: 36px;
      margin: 6px 0;
    }
  }
</style>
